import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Link } from 'react-router-dom';
import { ArrowLeft, User, Info, Link2, Shield, BookOpen, RotateCcw, Users } from 'lucide-react';
import { BrandWordmark } from '../components/brand/BrandWordmark';
import { useOnboarding } from '../components/onboarding/OnboardingContext';
import { FamilyGatheringRoomCard } from '../components/gathering';
import { SettingsAdvancedFeatures } from '../components/settings/SettingsAdvancedFeatures';
import { CORE_JOURNEY_UI } from '../config/testerRelease';

const sectionCard =
  'rounded-2xl border border-[#e8e2d8] bg-[#fffcf7]/90 shadow-sm';
const iconWrap = 'flex h-10 w-10 items-center justify-center rounded-xl bg-[#c9a961]/15 text-[#8b7355]';

const Settings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { requestWalkthroughReplay } = useOnboarding();

  return (
    <div className="min-h-screen bg-[#faf8f5]">
      <nav className="border-b border-[#e8e2d8]/90 bg-[#faf8f5]/95 backdrop-blur-sm">
        <div className="mx-auto flex max-w-4xl items-center gap-3 px-4 py-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="text-[#5c534a] hover:bg-[#ebe6dc]/60 hover:text-[#1c1917]"
            data-testid="back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-serif text-xl font-medium tracking-tight text-[#1c1917]" data-testid="settings-title">
            Account settings
          </h1>
        </div>
      </nav>

      <div className="mx-auto max-w-4xl space-y-6 px-4 py-10 sm:px-6 sm:py-12 lg:px-8">
        <p className="-mt-4 text-sm leading-relaxed text-[#5c534a]">
          Your profile and app preferences. To manage a specific family archive — name, privacy, exports, memorial
          defaults — open that archive and choose <strong className="font-medium text-[#1c1917]">Archive settings</strong>{' '}
          from the sidebar.
        </p>
        <Card className={sectionCard} data-testid="profile-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className={iconWrap}>
                <User className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <CardTitle className="font-serif text-lg font-normal text-[#1c1917]">Profile</CardTitle>
                <CardDescription className="text-[#5c534a]">Your account</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center gap-4 rounded-xl border border-[#e8e2d8]/80 bg-[#faf8f5]/60 px-4 py-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#c9a961]/15 ring-1 ring-[#c9a961]/20">
                <User className="h-8 w-8 text-[#8b7355]" aria-hidden />
              </div>
              <div>
                <p className="font-medium text-[#1c1917]" data-testid="user-name">
                  {user?.name}
                </p>
                <p className="text-sm text-[#5c534a]" data-testid="user-email">
                  {user?.email}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className={sectionCard} data-testid="privacy-trust-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className={iconWrap}>
                <Shield className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <CardTitle className="font-serif text-lg font-normal text-[#1c1917]">Privacy &amp; trust</CardTitle>
                <CardDescription className="text-[#5c534a]">How we treat your family data</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 pt-0">
            <p className="text-sm leading-relaxed text-[#5c534a]">
              We do not sell your family information. Your trees, stories, and documents are yours. You can export from
              any archive&apos;s settings — Legacy Book PDF or a full backup — whenever you need.
            </p>
            <ul className="space-y-2.5 text-sm text-[#4a433c]">
              <li className="flex items-start gap-2.5">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#c9a961]" aria-hidden />
                <span>DNA is optional; you control matching and can remove it anytime.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#c9a961]" aria-hidden />
                <span>Discovery is opt-in. Your tree stays private unless you choose otherwise.</span>
              </li>
              <li className="flex items-start gap-2.5">
                <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#c9a961]" aria-hidden />
                <span>Collaborators only see what you invite them to.</span>
              </li>
            </ul>
            <Link to="/faq">
              <Button
                variant="outline"
                size="sm"
                className="mt-1 rounded-full border-[#c9a961]/40 bg-transparent text-[#1c1917] hover:bg-[#faf8f5]"
              >
                <Info className="mr-2 h-4 w-4 text-[#8b7355]" />
                Read FAQ
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card className={sectionCard} data-testid="help-guide-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className={iconWrap}>
                <BookOpen className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <CardTitle className="font-serif text-lg font-normal text-[#1c1917]">Help &amp; guide</CardTitle>
                <CardDescription className="text-[#5c534a]">Learn what MyLegacyBridge can do</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2 pt-0">
            <Button asChild variant="outline" size="sm" className="rounded-full border-[#c9a961]/40">
              <Link to="/guide">
                <BookOpen className="mr-2 h-4 w-4 text-[#8b7355]" />
                Feature guide
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="rounded-full border-[#e8e2d8]">
              <Link to="/help">
                Help &amp; guides
              </Link>
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-full border-[#e8e2d8]"
              onClick={() => {
                requestWalkthroughReplay();
                navigate('/dashboard');
              }}
            >
              <RotateCcw className="mr-2 h-4 w-4 text-[#8b7355]" />
              Restart walkthrough
            </Button>
            <Button asChild variant="ghost" size="sm" className="rounded-full text-[#5c534a]">
              <Link to="/faq">FAQ &amp; support</Link>
            </Button>
            <Button asChild variant="ghost" size="sm" className="rounded-full text-[#5c534a]">
              <Link to="/help#family-gathering-room">
                <Users className="mr-2 h-4 w-4 text-[#8b7355]" />
                Family Gathering Room
              </Link>
            </Button>
          </CardContent>
        </Card>

        {!CORE_JOURNEY_UI && <FamilyGatheringRoomCard />}

        {CORE_JOURNEY_UI && <SettingsAdvancedFeatures />}

        <Card className={sectionCard} data-testid="connection-requests-card">
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={iconWrap}>
                  <Link2 className="h-5 w-5" aria-hidden />
                </div>
                <div>
                  <CardTitle className="font-serif text-lg font-normal text-[#1c1917]">Connection requests</CardTitle>
                  <CardDescription className="text-[#5c534a]">Family matches you&apos;ve chosen to explore</CardDescription>
                </div>
              </div>
              <Link to="/connection-requests">
                <Button
                  variant="outline"
                  size="sm"
                  className="shrink-0 rounded-full border-[#e8e2d8] bg-white/80 text-[#1c1917] hover:bg-[#faf8f5]"
                >
                  View
                </Button>
              </Link>
            </div>
          </CardHeader>
        </Card>

        <Card className={sectionCard} data-testid="about-card">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className={iconWrap}>
                <Info className="h-5 w-5" aria-hidden />
              </div>
              <div>
                <CardTitle className="font-serif text-lg font-normal text-[#1c1917]">About</CardTitle>
                <CardDescription className="text-[#5c534a]">
                  <BrandWordmark variant="subtle" className="text-base" />
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="leading-relaxed text-[#5c534a]">
              A place to preserve family stories and history—with care for the people who come after you.
            </p>
            <p className="mt-4 inline-flex flex-wrap items-baseline gap-x-1 text-sm text-[#8b7355]">
              <span>Version 1.0.0 · © 2026</span>
              <BrandWordmark
                variant="footerCopyrightLight"
                as="span"
                className="[&_.brand-wordmark__my]:text-[#8b7355]/85 [&_.brand-wordmark__bridge]:text-[#8b7355]"
              />
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
